import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useEffect, useRef } from 'react';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { ensureRenderableImage } from '@/utils/heif';
import { useCheckWelloApp } from './useCheckWelloApp';
interface UseFileUploadProps {
  accept?: string;
  multiple?: boolean;
}
export const useFileUpload = t0 => {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    accept,
    multiple: t2
  } = t1;
  const multiple = t2 === undefined ? false : t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      type: MESSAGE_KEYWORD.IMAGE
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    request,
    response
  } = useHandleApp(t3);
  const {
    isWelloApp
  } = useCheckWelloApp();
  const {
    toast
  } = useToast();
  const image64 = response?.data?.image;
  const resolveRef = useRef(null);
  const resolveFilesRef = useRef(null);
  const file = useRef(undefined);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const files = useRef(t4);
  const activeFileInput = useRef(null);
  let t5;
  let t6;
  if ($[4] !== image64 || $[5] !== toast) {
    t5 = () => {
      if (!image64) {
        return;
      }
      let isCancelled = false;
      const buildAndResolve = async () => {
        const byteCharacters = atob(image64);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset = offset + 512, offset) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let index = 0; index < slice.length; index++) {
            byteNumbers[index] = slice.codePointAt(index) ?? 0;
          }
          byteArrays.push(new Uint8Array(byteNumbers));
        }
        const blob = new Blob(byteArrays, {
          type: "image/png"
        });
        let resolvedFile;
        try {
          resolvedFile = await ensureRenderableImage(new File([blob], "image.png", {
            type: "image/png"
          }));
        } catch {
          if (!isCancelled) {
            toast({
              message: "\uC774\uBBF8\uC9C0 \uBCC0\uD658\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uB2E4\uB978 \uC0AC\uC9C4\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.",
              type: "fail"
            });
          }
        }
        if (isCancelled) {
          return;
        }
        file.current = resolvedFile;
        resolveRef.current?.(resolvedFile);
        resolveRef.current = null;
        files.current = resolvedFile ? [resolvedFile] : [];
        resolveFilesRef.current?.(files.current);
        resolveFilesRef.current = null;
      };
      buildAndResolve();
      return () => {
        isCancelled = true;
      };
    };
    t6 = [image64, toast];
    $[4] = image64;
    $[5] = toast;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== accept || $[9] !== isWelloApp || $[10] !== request || $[11] !== toast) {
    t7 = pickMultiple => {
      resolveRef.current?.(undefined);
      resolveRef.current = null;
      resolveFilesRef.current?.([]);
      resolveFilesRef.current = null;
      file.current = undefined;
      files.current = [];
      activeFileInput.current?.remove();
      activeFileInput.current = null;
      if (isWelloApp) {
        request();
      } else {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        if (accept) {
          fileInput.accept = accept;
        }
        if (pickMultiple) {
          fileInput.multiple = true;
        }
        fileInput.className = "sr-only";
        const removeFileInput = () => {
          fileInput.remove();
          if (activeFileInput.current === fileInput) {
            activeFileInput.current = null;
          }
        };
        fileInput.addEventListener("change", async event => {
          const input = event.target as HTMLInputElement;
          if (input.files && input.files.length > 0) {
            const selectedFiles = Array.from(input.files);
            const validFiles = [];
            for (const selectedFile of selectedFiles) {
              try {
                validFiles.push(await ensureRenderableImage(selectedFile));
              } catch {
                continue;
              }
            }
            if (validFiles.length < selectedFiles.length) {
              toast({
                message: "\uC77C\uBD80 \uC774\uBBF8\uC9C0\uB97C \uBCC0\uD658\uD558\uC9C0 \uBABB\uD588\uC5B4\uC694.",
                type: "fail"
              });
            }
            file.current = validFiles[0];
            files.current = validFiles;
          }
          resolveRef.current?.(file.current);
          resolveRef.current = null;
          resolveFilesRef.current?.(files.current);
          resolveFilesRef.current = null;
          removeFileInput();
        });
        fileInput.addEventListener("cancel", () => {
          resolveRef.current?.(undefined);
          resolveRef.current = null;
          resolveFilesRef.current?.([]);
          resolveFilesRef.current = null;
          removeFileInput();
        });
        activeFileInput.current = fileInput;
        document.body.append(fileInput);
        fileInput.click();
      }
    };
    $[8] = accept;
    $[9] = isWelloApp;
    $[10] = request;
    $[11] = toast;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const openFilePicker = t7;
  let t8;
  if ($[13] !== openFilePicker) {
    t8 = async () => {
      openFilePicker(false);
      return new Promise(resolve => {
        resolveRef.current = resolve;
      });
    };
    $[13] = openFilePicker;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== multiple || $[16] !== openFilePicker) {
    t9 = async () => {
      openFilePicker(multiple);
      return new Promise(resolve_0 => {
        resolveFilesRef.current = resolve_0;
      });
    };
    $[15] = multiple;
    $[16] = openFilePicker;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = {
      getFile: t8,
      getFiles: t9
    };
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};